import { buildSchedule } from '../domain/schedule.js';
import { summarize } from '../domain/metrics.js';
import { ScenarioSummary, ComparisonResult } from '../domain/types.js';
import { FormState, buildScenario } from './form-utils.js';
import {
  formatRub,
  formatSignedRub,
  formatMonthIn,
  formatMonthFrom,
  formatMonthRange,
  formatPercent,
  formatTerm,
} from './format.js';
import { el } from './dom.js';
import { t } from '../features/i18n/index.js';

export type DiffTone = 'diff-good' | 'diff-bad' | 'diff-neutral';

function setText(id: string, value: string): void {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

export type KpiValues = { monthly: string; totalInterest: string; termLabel: string };

/** Writes the sticky KPI bar (`#kpi-bar`, phones) values when its markup exists; no-op otherwise. */
export function updateKpiBar({ monthly, totalInterest, termLabel }: KpiValues): void {
  setText('kpi-monthly', monthly);
  setText('kpi-interest', totalInterest);
  setText('kpi-term', termLabel);
}

/** Total interest as a whole-percent share of the principal (0 for no principal / no interest). */
export function interestSharePercent(totalInterest: number, principal: number): number {
  return principal > 0 && totalInterest > 0 ? Math.round((totalInterest / principal) * 100) : 0;
}

/** «переплата 64 % от суммы кредита» — the balance chart tag. */
export function overpaymentRatioLabel(totalInterest: number, principal: number): string {
  const ratio = interestSharePercent(totalInterest, principal);
  return `переплата ${ratio.toLocaleString('ru-RU')}\u00a0% от суммы кредита`;
}

/** Colour of a delta: negative is good by default (less money, shorter term). */
export function tone(delta: number, lowerIsBetter = true): DiffTone {
  if (delta === 0) return 'diff-neutral';
  const good = lowerIsBetter ? delta < 0 : delta > 0;
  return good ? 'diff-good' : 'diff-bad';
}

export type NotePart = { text: string; tone?: DiffTone };

export const NOTE_NO_EVENTS = 'Это ваш кредит без досрочных платежей и льгот. Добавьте событие ниже — увидите, что изменится.';
export const NOTE_IS_BASELINE = 'Это базовый сценарий для сравнения. Откройте другой сценарий, чтобы увидеть разницу.';
export const NOTE_SAME_AS_BASELINE = 'Переплата такая же, как в базовом сценарии.';

/**
 * Sentence under the result tiles. Priority: the form holds the baseline → a baseline
 * exists (sign convention of the comparison table: minus = less overpayment = lime,
 * plus = more = ember) → events are on (deltas against the same loan without any event)
 * → the plain loan. Returned as parts so the amounts can carry a colour.
 */
export function resultNoteParts(
  state: FormState,
  summary: ScenarioSummary,
  comparison?: ComparisonResult,
  isBaseline = false,
): NotePart[] {
  if (isBaseline) return [{ text: NOTE_IS_BASELINE }];

  if (comparison) {
    const delta = -comparison.savingsVsBase;
    if (delta === 0) return [{ text: NOTE_SAME_AS_BASELINE }];
    return [
      { text: 'Изменение переплаты против базового: ' },
      { text: formatSignedRub(delta), tone: tone(delta) },
      { text: ` (${delta < 0 ? 'меньше' : 'больше'}, чем в базовом сценарии).` },
    ];
  }

  const plain = summarizeScenarioWithoutExtras(state);
  if (!plain) return [{ text: NOTE_NO_EVENTS }];

  const interestDelta = summary.totalInterest - plain.totalInterest;
  const termDelta = summary.actualTermMonths - plain.actualTermMonths;
  const parts: NotePart[] = [{ text: 'С учётом событий: ' }];
  if (interestDelta === 0) {
    parts.push({ text: 'переплата не меняется' });
  } else {
    parts.push(
      { text: 'переплата на ' },
      { text: formatRub(Math.abs(interestDelta)), tone: tone(interestDelta) },
      { text: interestDelta < 0 ? ' меньше' : ' больше' },
    );
  }
  parts.push({ text: ', ' });
  if (termDelta === 0) {
    parts.push({ text: 'срок не меняется' });
  } else {
    parts.push(
      { text: 'кредит закроется на ' },
      { text: formatTerm(Math.abs(termDelta)), tone: tone(termDelta) },
      { text: termDelta < 0 ? ' раньше' : ' позже' },
    );
  }
  parts.push({ text: ' — по сравнению с этим же кредитом без событий.' });
  return parts;
}

/** Plain-language sentences for the scenario log (`#scenario-log`); one `<p>` per sentence. */
export function scenarioLogSentences(state: FormState, summary: ScenarioSummary): string[] {
  const rub = (value: number) => formatRub(Math.round(value * 100));
  const entries: string[] = [];
  entries.push(`Кредит ${rub(state.principal)} под ${formatPercent(state.rateApr)} годовых, первый платёж в ${formatMonthIn(state.startDate)}.`);
  if (state.calculationMode === 'bank_calendar') {
    entries.push(t('bank.log', { day: state.bankPaymentDay ?? 8 }));
  }
  if (state.discountEnabled) {
    entries.push(`Льготная ставка ${formatPercent(state.discountRate)} ${formatMonthRange(state.discountStart, state.discountEnd)} включительно.`);
  }
  if (state.overpaymentEnabled) {
    if (state.overpaymentType === 'fixed' && state.recurringAmount > 0) {
      const period = state.recurringEnd
        ? formatMonthRange(state.recurringStart, state.recurringEnd)
        : `с ${formatMonthFrom(state.recurringStart)} до конца кредита`;
      entries.push(`Каждый месяц досрочно ${rub(state.recurringAmount)} ${period}.`);
    } else if (state.overpaymentType === 'adaptive' && state.adaptiveTargetAmount > 0) {
      const period = state.adaptiveEnd
        ? formatMonthRange(state.adaptiveStart, state.adaptiveEnd)
        : `с ${formatMonthFrom(state.adaptiveStart)} до конца кредита`;
      entries.push(`Каждый месяц ориентир общей суммы ${rub(state.adaptiveTargetAmount)} (досрочно не меньше ${rub(state.adaptiveMinAmount)}) ${period}.`);
    }
  }
  if (state.lumpEnabled) {
    state.lumps
      .filter(lump => lump.amount > 0 && lump.month)
      .forEach(lump => {
        // YYYY-MM strings order chronologically; a lump after the payoff month never reaches the schedule.
        const skipped = lump.month > summary.payoffDate ? ' — не учтён: к этому месяцу кредит уже закрыт' : '';
        entries.push(`Досрочный платёж ${rub(lump.amount)} в ${formatMonthIn(lump.month)}${skipped}.`);
      });
  }
  if (state.refinanceEnabled) {
    if (state.refinanceMonth > summary.payoffDate) {
      entries.push(`Рефинансирование в ${formatMonthIn(state.refinanceMonth)} не учтено: кредит закрыт раньше.`);
    } else {
      entries.push(`Рефинансирование в ${formatMonthIn(state.refinanceMonth)}: ${formatPercent(state.refinanceRate)} годовых на ${formatTerm(Math.round(state.refinanceTerm * 12))}.`);
    }
  }
  const term = formatTerm(summary.actualTermMonths);
  // «7 лет · 2 мес.» already ends with a period; do not double it.
  entries.push(`Закроете кредит в ${formatMonthIn(summary.payoffDate)} — срок по расчёту ${term}${term.endsWith('.') ? '' : '.'}`);
  return entries;
}

export function renderLog(state: FormState, summary: ScenarioSummary): void {
  const log = document.getElementById('scenario-log');
  if (!log) return;
  const heading = el('h3', {}, ['Что заложено в сценарий']);
  log.replaceChildren(heading, ...scenarioLogSentences(state, summary).map(line => el('p', {}, [line])));
}

/** Summary of the same loan with every extra (discount, overpayments, refinance) switched off. */
export function summarizeScenarioWithoutExtras(state: FormState): ScenarioSummary | null {
  if (!state.discountEnabled && !state.overpaymentEnabled && !state.lumpEnabled && !state.refinanceEnabled) {
    return null;
  }
  const plainState: FormState = {
    ...state,
    discountEnabled: false,
    overpaymentEnabled: false,
    lumpEnabled: false,
    refinanceEnabled: false,
  };
  const plainScenario = buildScenario(plainState);
  const baseline = { ...plainScenario, id: 'baseline' };
  return summarize(baseline, buildSchedule(baseline).months);
}

